import { RestError } from '@azure/storage-blob';
import { assertCanHandle } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { TimeoutError } from '#common/types';
import { waitForCondition } from '#common/util';
import { AuthorizationError, BucketNotEmptyError, BucketNotFoundError, ObjectNotFoundError, ObjectStoreError, ObjectStoreStats, } from '#object-store/types';
import { fetchObject } from '#object-strore/azure-get-object';
import { SasTokenBlobClientProvider } from './sas-token-blob-client-provider';
export const DEFAULT_RETRY_CONFIG = {
    initialDelay: 4000,
};
export const createAzureStoreWithSasTokens = async (providerConfig, retryOptions = DEFAULT_RETRY_CONFIG) => {
    const blobClientProvider = new SasTokenBlobClientProvider(providerConfig, retryOptions);
    await blobClientProvider.setUp();
    return new AzureStore(blobClientProvider);
};
export const createAzureStore = (blobClientProvider) => {
    return new AzureStore(blobClientProvider);
};
function getRestError(restError, context) {
    if (restError.code == 'ContainerNotFound') {
        return new BucketNotFoundError(context.bucketName);
    }
    else if (restError.statusCode == 404) {
        return new ObjectNotFoundError(context.objectKey);
    }
    else if (restError.statusCode == 403) {
        return new AuthorizationError();
    }
    else {
        return new ObjectStoreError(`Error code: ${restError.code}, message: '${restError.message}'.`);
    }
}
async function executeGuardedAndRethrow(fn, context, log) {
    try {
        return await fn();
    }
    catch (err) {
        assertCanHandle(err, (e) => e instanceof RestError);
        log.trace(`RestError with statusCode: ${err.statusCode}`);
        throw getRestError(err, context);
    }
}
class AzureStore {
    blobClientProvider;
    log = Logging.getLogger('AzureStore');
    statistics = new ObjectStoreStats(this.log);
    constructor(blobClientProvider) {
        this.blobClientProvider = blobClientProvider;
    }
    async bucketExists(bucketName, _objectPrefix) {
        return withStats(this.statistics.bucketExists, async () => {
            const blobServiceClient = this.blobClientProvider.getClient();
            const containerClient = blobServiceClient.getContainerClient(bucketName);
            const containerExists = async () => {
                return await containerClient.exists();
            };
            const context = { bucketName };
            return await executeGuardedAndRethrow(containerExists, context, this.log);
        }, { bucketName });
    }
    async createBucket(bucketName, objectPrefix) {
        await withStats(this.statistics.createBucket, async () => {
            const blobServiceClient = this.blobClientProvider.getClient();
            const containerClient = blobServiceClient.getContainerClient(bucketName);
            const createContainer = async () => {
                await containerClient.createIfNotExists();
            };
            const context = { bucketName };
            await executeGuardedAndRethrow(createContainer, context, this.log);
            this.log.trace(`Container ${bucketName} successfully created`);
        }, { bucketName });
        await this.ensureBucketExists(bucketName, objectPrefix);
    }
    async deleteBucket(bucketName, _objectPrefix) {
        return withStats(this.statistics.deleteBucket, async () => {
            const blobServiceClient = this.blobClientProvider.getClient();
            const containerClient = blobServiceClient.getContainerClient(bucketName);
            const deleteContainer = async () => {
                const blobs = containerClient.listBlobsFlat();
                const it = await blobs.next();
                if (!it.done) {
                    throw new BucketNotEmptyError(bucketName);
                }
                await containerClient.delete();
            };
            const context = { bucketName };
            await executeGuardedAndRethrow(deleteContainer, context, this.log);
            this.log.trace(`Container ${bucketName} successfully deleted`);
        }, { bucketName });
    }
    async headObject(bucketName, objectKey) {
        return withStats(this.statistics.headObject, async () => {
            const blobClient = this.getBlobClient(bucketName, objectKey);
            const getBlobProperties = async () => {
                const properties = await blobClient.getProperties();
                const objectInfo = {
                    objectKey: objectKey,
                    lastModTime: properties.lastModified.getTime(),
                    objectSize: properties.contentLength,
                };
                this.log.trace(`Blob header: { objectKey: ${objectInfo.objectKey}, lastModTime: ${objectInfo.lastModTime}, objectSize: ${objectInfo.objectSize} }`);
                return objectInfo;
            };
            const context = { bucketName, objectKey };
            return await executeGuardedAndRethrow(getBlobProperties, context, this.log);
        }, { bucketName, objectKey });
    }
    async getObject(bucketName, objectKey, offset, size) {
        return withStats(this.statistics.getObject, async () => {
            const blobClient = this.getBlobClient(bucketName, objectKey);
            const getBuffer = () => {
                return fetchObject(blobClient, offset, size);
            };
            const context = { bucketName, objectKey };
            return await executeGuardedAndRethrow(getBuffer, context, this.log);
        }, { bucketName, objectKey, offset, size });
    }
    async putObject(bucketName, objectKey, data) {
        return withStats(this.statistics.putObject, async () => {
            const blockBlobClient = this.getBlobClient(bucketName, objectKey);
            const uploadData = async () => {
                await blockBlobClient.uploadData(data);
            };
            const context = { bucketName, objectKey };
            await executeGuardedAndRethrow(uploadData, context, this.log);
            this.log.trace(`Blob ${objectKey} successfully created`);
        }, { bucketName, objectKey, size: data.length });
    }
    async deleteObject(bucketName, objectKey) {
        return withStats(this.statistics.deleteObject, async () => {
            const blobClient = this.getBlobClient(bucketName, objectKey);
            const deleteBlob = async () => {
                await blobClient.deleteIfExists();
            };
            const context = { bucketName, objectKey };
            await executeGuardedAndRethrow(deleteBlob, context, this.log);
            this.log.trace(`Blob ${objectKey} successfully deleted`);
        }, { bucketName, objectKey });
    }
    stats() {
        return this.statistics;
    }
    stop() {
        this.blobClientProvider.stop();
    }
    getBlobClient(bucketName, objectKey) {
        const blobServiceClient = this.blobClientProvider.getClient();
        const containerClient = blobServiceClient.getContainerClient(bucketName);
        return containerClient.getBlockBlobClient(objectKey);
    }
    async ensureBucketExists(bucketName, objectPrefix) {
        const numIterations = 10;
        const sleepTimeMs = 1000;
        try {
            await waitForCondition(() => {
                return this.bucketExists(bucketName, objectPrefix);
            }, numIterations, sleepTimeMs);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof TimeoutError);
            throw new ObjectStoreError('Timed out while waiting for the bucket to get created.');
        }
    }
}
